import type { EffectiveThemeMode } from "./palette";

import * as React from "react";

const DOT_SPACING = 16;
const DOT_RADIUS = 0.8;

const DOT_COLORS: Record<EffectiveThemeMode, string> = {
  dark: "rgba(255, 255, 255, 0.12)",
  light: "rgba(0, 0, 0, 0.08)",
};

function drawDotGrid(canvas: HTMLCanvasElement, dotColor: string) {
  const dpr = window.devicePixelRatio ?? 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return;
  }

  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, rect.width, rect.height);
  ctx.fillStyle = dotColor;

  for (let x = DOT_SPACING / 2; x < rect.width; x += DOT_SPACING) {
    for (let y = DOT_SPACING / 2; y < rect.height; y += DOT_SPACING) {
      ctx.beginPath();
      ctx.arc(x, y, DOT_RADIUS, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

export function DotGridBackground({ mode }: { mode: EffectiveThemeMode }) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const dotColor = DOT_COLORS[mode];

  // eslint-disable-next-line replo/no-use-effect -- Canvas needs imperative drawing on mount and on resize.
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    drawDotGrid(canvas, dotColor);

    const observer = new ResizeObserver(() => {
      drawDotGrid(canvas, dotColor);
    });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [dotColor]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className="pointer-events-none absolute inset-0 h-full w-full"
    />
  );
}
